// The move-list toggle: a canvas icon beside the gear, drawn in the same two
// colours. Three lines when the list is showing; when it is hidden the lines
// dim and a slash crosses them, so the state never rests on brightness alone.
import { roundedRect, type GearIconBounds, type GearIconPalette } from '@bn-games/render-helpers';

export interface MoveListIconOptions {
  readonly hover: boolean;
  /** Whether the move list is currently shown. */
  readonly active: boolean;
}

const PLATE_RATIO = 0.84;
const PLATE_RADIUS_RATIO = 0.2;
const LINE_INSET_RATIO = 0.26;
const LINE_ROWS: readonly number[] = [0.34, 0.5, 0.66];
// Dimmed, but still 3.5:1 or better against the dark surround in every theme.
const INACTIVE_ALPHA = 0.55;
const SLASH_INSET_RATIO = 0.2;
// Stroke weights and hover glow, as shares of the icon's size (the gear uses the same two).
const OUTLINE_WIDTH_RATIO = 0.04;
const HOVER_GLOW_RATIO = 0.18;
const LINE_WIDTH_RATIO = 0.07;
const MIN_OUTLINE_WIDTH = 1;
const MIN_LINE_WIDTH = 1.5;

/** Draw the icon in `[x, x+size] × [y, y+size]`; the returned bounds hit-test like the gear's. */
export function drawMoveListIcon(
  ctx: CanvasRenderingContext2D,
  x: number,
  y: number,
  size: number,
  palette: GearIconPalette,
  opts: MoveListIconOptions,
): GearIconBounds {
  const plate = size * PLATE_RATIO;
  const inset = (size - plate) / 2;
  ctx.save();
  ctx.fillStyle = palette.fill;
  ctx.strokeStyle = palette.border;
  ctx.lineWidth = Math.max(MIN_OUTLINE_WIDTH, size * OUTLINE_WIDTH_RATIO);
  if (opts.hover) {
    ctx.shadowColor = palette.border;
    ctx.shadowBlur = size * HOVER_GLOW_RATIO;
  }
  roundedRect(ctx, x + inset, y + inset, plate, plate, plate * PLATE_RADIUS_RATIO);
  ctx.fill();
  ctx.stroke();

  ctx.shadowColor = 'transparent';
  ctx.shadowBlur = 0;
  ctx.globalAlpha = opts.active ? 1 : INACTIVE_ALPHA;
  ctx.lineWidth = Math.max(MIN_LINE_WIDTH, size * LINE_WIDTH_RATIO);
  ctx.lineCap = 'round';
  for (const row of LINE_ROWS) {
    ctx.beginPath();
    ctx.moveTo(x + size * LINE_INSET_RATIO, y + size * row);
    ctx.lineTo(x + size * (1 - LINE_INSET_RATIO), y + size * row);
    ctx.stroke();
  }
  if (!opts.active) {
    ctx.globalAlpha = 1;
    ctx.beginPath();
    ctx.moveTo(x + size * SLASH_INSET_RATIO, y + size * (1 - SLASH_INSET_RATIO));
    ctx.lineTo(x + size * (1 - SLASH_INSET_RATIO), y + size * SLASH_INSET_RATIO);
    ctx.stroke();
  }
  ctx.restore();
  return { x, y, size };
}
